/**
 * Portal crypto orchestration — the ONLY place the web app composes
 * `@heivol-locker/crypto` primitives into enrolment / unlock / item operations.
 * Everything here runs client-side in tab memory; nothing plaintext is returned
 * to the caller except decrypted payloads it explicitly asks for.
 *
 * The `vault-client` carries the resulting opaque blobs; it never sees a key.
 */
import { ARGON2ID_PARAMS_V1, decryptItem, deriveKek, deriveUmkProofKey, deviceSafetyNumber, encryptItem, generateDeviceKeypair, generateItemDek, generateRecoveryCode, generateUmk, KindByte, openDeviceEnvelope, sealDeviceEnvelope, unwrapDek, unwrapUmk, wrapDek, wrapUmk, } from '@heivol-locker/crypto';
import { fromBase64, sha256, toBase64, utf8, uuidToBytes, uuidv4, } from '@heivol-locker/vault-client';
// Production KDF cost (spec §2). A 512 MiB Argon2id derivation takes ~0.7s in a
// browser — surfaced behind a spinner on enrol/unlock.
const PARAMS = ARGON2ID_PARAMS_V1;
const decoder = new TextDecoder();
// AAD content version. NOTE(phase-3 item-edit): encrypt and decrypt MUST pass the
// SAME value or AES-GCM auth fails. v1 has no edit path so both sides pin 1. When
// item editing lands (server bumps `version` on PATCH), bind both sides to the
// item's persisted version (or an explicit content-version column) — never let
// the encrypt-time and decrypt-time values drift, or items decrypt-brick.
const CONTENT_VERSION_V1 = 1;
/** Concatenate byte arrays (for hash / AAD preimages). */
function concatBytes(...parts) {
    const total = parts.reduce((n, p) => n + p.length, 0);
    const out = new Uint8Array(total);
    let off = 0;
    for (const p of parts) {
        out.set(p, off);
        off += p.length;
    }
    return out;
}
/** Build the full `/v1/enroll` request + the resulting unlock session. */
export async function buildEnrolment(passphrase) {
    const userId = uuidv4();
    const userIdBytes = uuidToBytes(userId);
    const saltPass = crypto.getRandomValues(new Uint8Array(16));
    const saltRec = crypto.getRandomValues(new Uint8Array(16));
    const umk = await generateUmk();
    const passEnvId = uuidv4();
    const recEnvId = uuidv4();
    const passBlob = await wrapUmk({
        umk,
        kek: await deriveKek({ passphrase: utf8(passphrase), salt: saltPass, params: PARAMS }),
        kind: KindByte.EnvelopePassphrase,
        userId: userIdBytes,
        envelopeId: uuidToBytes(passEnvId),
    });
    const recoveryCode = await generateRecoveryCode();
    const recBlob = await wrapUmk({
        umk,
        kek: await deriveKek({ passphrase: utf8(recoveryCode), salt: saltRec, params: PARAMS }),
        kind: KindByte.EnvelopeRecovery,
        userId: userIdBytes,
        envelopeId: uuidToBytes(recEnvId),
    });
    const request = {
        userId,
        argon2SaltPass: toBase64(saltPass),
        argon2SaltRec: toBase64(saltRec),
        params: PARAMS,
        passphraseEnvelope: {
            id: passEnvId,
            kind: 'passphrase',
            deviceId: null,
            blob: toBase64(passBlob),
        },
        recoveryEnvelope: { id: recEnvId, kind: 'recovery', deviceId: null, blob: toBase64(recBlob) },
        // TODO(phase-1 recovery flow): crypto-spec §6 mandates an *Argon2id* verifier
        // over a dedicated salt (NOT the recovery KEK salt — reusing it would equal
        // KEK_recovery and hand the server vault access). A plain SHA-256 over a
        // 256-bit BIP-39 code is not brute-forceable, but it deviates from the frozen
        // spec. Build the salted-Argon2id verifier (+ a `recoveryVerifierSalt` wire
        // field) together with the recovery-complete flow, which is its only consumer.
        recoveryCodeVerifier: toBase64(await sha256(utf8(recoveryCode))),
        umkProofKey: toBase64(await deriveUmkProofKey(umk, userIdBytes)),
        device: {
            id: uuidv4(),
            name: 'Web portal',
            kind: 'web-portal',
            // Placeholder X25519 pubkey — real device pairing is a later Phase-3 step.
            publicKey: toBase64(crypto.getRandomValues(new Uint8Array(32))),
        },
    };
    return { request, session: { umk, userId, userIdBytes }, recoveryCode };
}
/** Re-derive the KEK and unwrap a fresh UMK from the passphrase envelope. */
export async function unlockSession(passphrase, env) {
    const kek = await deriveKek({
        passphrase: utf8(passphrase),
        salt: fromBase64(env.argon2SaltPass),
        params: env.params,
    });
    const umk = await unwrapUmk({
        blob: fromBase64(env.passphraseEnvelope.blob),
        kek,
        expectedKind: KindByte.EnvelopePassphrase,
        userId: uuidToBytes(env.userId),
        envelopeId: uuidToBytes(env.passphraseEnvelope.id),
    });
    return { umk, userId: env.userId, userIdBytes: uuidToBytes(env.userId) };
}
/**
 * Rebuild an `UnlockSession` from an already-unwrapped UMK — no passphrase, no
 * Argon2id. Used to resume a still-unlocked session (extension EXT-6) after the
 * derived UMK was cached under an in-memory-only wrapping key. The UMK bytes must
 * come from a trusted local cache, never the wire; this only re-derives the
 * cheap `userIdBytes` view and re-brands the buffer.
 */
export function rehydrateSession(umk, userId) {
    return { umk: umk, userId, userIdBytes: uuidToBytes(userId) };
}
/**
 * Canonicalise a typed-in recovery code so it matches the byte-exact form the
 * KEK + verifier were derived from at enrolment: BIP-39 English words are
 * lowercase, single-space-joined. Tolerates stray casing / newlines / double
 * spaces from copy-paste without changing the semantic code.
 */
function normalizeRecoveryCode(raw) {
    return raw.trim().toLowerCase().split(/\s+/u).join(' ');
}
/**
 * Recover a locked-out vault with the 24-word recovery code (portal-spec §4.8).
 * Unwraps the UMK from the recovery envelope (KEK_recovery), then re-seals the
 * SAME UMK under a brand-new passphrase envelope (fresh salt + envelope id).
 * Items are never touched — only the passphrase envelope + salt change, same as
 * a rotation (INV-14). Returns the `/v1/recovery/complete` request plus the
 * resulting unlock session: on success the user lands unlocked.
 */
export async function recoverWithCode(recoveryCode, newPassphrase, start) {
    const code = normalizeRecoveryCode(recoveryCode);
    const userIdBytes = uuidToBytes(start.userId);
    // 1. Recover the UMK via KEK_recovery (recovery code + server-held salt/params).
    const umk = await unwrapUmk({
        blob: fromBase64(start.recoveryEnvelope.blob),
        kek: await deriveKek({
            passphrase: utf8(code),
            salt: fromBase64(start.argon2SaltRec),
            params: start.params,
        }),
        expectedKind: KindByte.EnvelopeRecovery,
        userId: userIdBytes,
        envelopeId: uuidToBytes(start.recoveryEnvelope.id),
    });
    // 2. Re-seal the SAME UMK under a fresh passphrase envelope. Derive KEK_pass
    //    with the account's stored params (start.params) — recovery/complete does
    //    NOT change users.params, so a later unlock must re-derive with the same.
    const saltPass = crypto.getRandomValues(new Uint8Array(16));
    const passEnvId = uuidv4();
    const passBlob = await wrapUmk({
        umk,
        kek: await deriveKek({ passphrase: utf8(newPassphrase), salt: saltPass, params: start.params }),
        kind: KindByte.EnvelopePassphrase,
        userId: userIdBytes,
        envelopeId: uuidToBytes(passEnvId),
    });
    const request = {
        // Matches the enrol-time verifier (SHA-256 over the code — see buildEnrolment
        // TODO: crypto-spec §6 wants a salted Argon2id verifier; keep enrol + recovery
        // in lockstep when that lands).
        recoveryProof: toBase64(await sha256(utf8(code))),
        newPassphraseEnvelope: {
            id: passEnvId,
            kind: 'passphrase',
            deviceId: null,
            blob: toBase64(passBlob),
        },
        argon2SaltPass: toBase64(saltPass),
        umkProofKey: toBase64(await deriveUmkProofKey(umk, userIdBytes)),
    };
    return { request, session: { umk, userId: start.userId, userIdBytes } };
}
/** Encrypt an item payload under a fresh per-item DEK wrapped by the UMK. */
export async function encryptItemPayload(session, vaultId, itemId, payload) {
    const dek = await generateItemDek();
    const ciphertext = await encryptItem({
        plaintext: utf8(JSON.stringify(payload)),
        dek,
        userId: session.userIdBytes,
        vaultId: uuidToBytes(vaultId),
        itemId: uuidToBytes(itemId),
        contentVersion: CONTENT_VERSION_V1,
    });
    const wrappedDek = await wrapDek({
        dek,
        umk: session.umk,
        userId: session.userIdBytes,
        itemId: uuidToBytes(itemId),
    });
    const domainHash = payload.kind === 'login' && payload.urls[0] !== undefined
        ? await domainHashFor(session, payload.urls[0])
        : null;
    return { ciphertext: toBase64(ciphertext), wrappedDek: toBase64(wrappedDek), domainHash };
}
/** Decrypt an item record back to its typed payload. */
export async function decryptItemRecord(session, item) {
    const dek = await unwrapDek({
        wrapped: fromBase64(item.wrappedDek),
        umk: session.umk,
        userId: session.userIdBytes,
        itemId: uuidToBytes(item.id),
    });
    const plaintext = await decryptItem({
        blob: fromBase64(item.ciphertext),
        dek,
        userId: session.userIdBytes,
        vaultId: uuidToBytes(item.vaultId),
        itemId: uuidToBytes(item.id),
        contentVersion: CONTENT_VERSION_V1,
    });
    return JSON.parse(decoder.decode(plaintext));
}
/** Encrypt a vault name under a per-vault DEK wrapped by the UMK. */
export async function encryptVaultName(session, vaultId, name) {
    const dek = await generateItemDek();
    const nameBlob = await encryptItem({
        plaintext: utf8(name),
        dek,
        userId: session.userIdBytes,
        vaultId: uuidToBytes(vaultId),
        itemId: uuidToBytes(vaultId),
        contentVersion: CONTENT_VERSION_V1,
    });
    const wrappedDek = await wrapDek({
        dek,
        umk: session.umk,
        userId: session.userIdBytes,
        itemId: uuidToBytes(vaultId),
    });
    return { nameBlob: toBase64(nameBlob), wrappedDek: toBase64(wrappedDek) };
}
/** Decrypt a vault name. */
export async function decryptVaultName(session, vault) {
    const dek = await unwrapDek({
        wrapped: fromBase64(vault.wrappedDek),
        umk: session.umk,
        userId: session.userIdBytes,
        itemId: uuidToBytes(vault.id),
    });
    const plaintext = await decryptItem({
        blob: fromBase64(vault.nameBlob),
        dek,
        userId: session.userIdBytes,
        vaultId: uuidToBytes(vault.id),
        itemId: uuidToBytes(vault.id),
        contentVersion: CONTENT_VERSION_V1,
    });
    return decoder.decode(plaintext);
}
/**
 * Naive eTLD+1 (last two labels). INV-6 requires a real Public Suffix List bind
 * for autofill — that lands with the extension (Phase-3 step 6); this stand-in
 * is fine for portal-side `domain_hash` computation in the shell.
 */
export function eTldPlusOne(url) {
    let host;
    try {
        host = new URL(url.includes('://') ? url : `https://${url}`).hostname;
    }
    catch {
        host = url;
    }
    const parts = host.split('.').filter((p) => p.length > 0);
    return parts.length <= 2 ? host : parts.slice(-2).join('.');
}
/**
 * Salted autofill-lookup hash, per crypto-spec §6:
 * `SHA-256("origin-v1" ‖ userId ‖ eTLD+1)`. The per-user salt makes the hash
 * non-precomputable and distinct across users, so the server cannot dictionary
 * the low-entropy eTLD+1 to learn which sites a user holds (metadata
 * confidentiality). The extension's `/v1/items/lookup` path MUST compute this
 * exact preimage. (eTLD+1 is still a naive stand-in pending the PSL bind — INV-6.)
 */
export async function domainHashFor(session, url) {
    const preimage = concatBytes(utf8('origin-v1'), session.userIdBytes, utf8(eTldPlusOne(url)));
    return toBase64(await sha256(preimage));
}
/** Generate this device's long-term X25519 identity + a fresh device id (§12.1). */
export function newDeviceIdentity() {
    const kp = generateDeviceKeypair();
    return {
        deviceId: uuidv4(),
        publicKey: toBase64(kp.publicKey),
        privateKey: toBase64(kp.privateKey),
    };
}
/**
 * Out-of-band MITM safety number for a device public key (§12.5). Both devices
 * render this from the same `DK_pub_new`; the user compares them and aborts on a
 * mismatch (a relay that swapped the key).
 */
export function pairingSafetyNumber(userId, devicePublicKey) {
    return deviceSafetyNumber(uuidToBytes(userId), fromBase64(devicePublicKey));
}
/**
 * Approver side — seal the unlocked UMK for a new device's public key into a
 * `kind='device'` envelope (§12.2). Returns the {@link EnvelopeWire} the approve
 * request carries; the server stores it verbatim and relays it on claim. The
 * approver chooses `newDeviceId` + a fresh envelope id — both are bound into the
 * KEK info / AAD and echoed on the wire so the new device can open it.
 */
export async function sealUmkForNewDevice(session, args) {
    const envelopeId = uuidv4();
    const blob = await sealDeviceEnvelope({
        umk: session.umk,
        newDevicePublicKey: fromBase64(args.newDevicePublicKey),
        userId: session.userIdBytes,
        deviceId: uuidToBytes(args.newDeviceId),
        envelopeId: uuidToBytes(envelopeId),
    });
    return { id: envelopeId, kind: 'device', deviceId: args.newDeviceId, blob: toBase64(blob) };
}
/**
 * New-device side — open the relayed device envelope with this device's private
 * key (§12.4) and rebuild an `UnlockSession`. The device then holds the UMK
 * exactly as after a passphrase unlock. `deviceId`/`envelopeId` come from the
 * wire (the approver chose them); a mismatch, wrong key, or tamper surfaces as
 * an `EnvelopeAuthTagMismatchError` from the crypto layer.
 */
export async function openDeviceEnvelopeToSession(userId, args) {
    const env = args.deviceEnvelope;
    if (env.deviceId === null) {
        throw new Error('device envelope missing deviceId');
    }
    const userIdBytes = uuidToBytes(userId);
    const umk = await openDeviceEnvelope({
        blob: fromBase64(env.blob),
        devicePrivateKey: fromBase64(args.devicePrivateKey),
        userId: userIdBytes,
        deviceId: uuidToBytes(env.deviceId),
        envelopeId: uuidToBytes(env.id),
    });
    return { umk, userId, userIdBytes };
}
/** Best-effort in-memory wipe of an unlock session's key material. */
export function wipeSession(session) {
    session.umk.fill(0);
    session.userIdBytes.fill(0);
}
//# sourceMappingURL=index.js.map